// A land made of real art — built the same way as Capital City: a pieces .glb (each unique mesh once) + a layout .json (where every
// piece sits, plus collision footprints), drawn with InstancedMesh in spatial chunks so off-screen parts are culled.
// The layout is produced by scripts/adventure/make_adventure.mjs (docs/ADVENTURES.md); the manifest (land.json) says which files to load.
//
// Two ways a piece can get its look:
//   • palette  — Capital-style: the piece names a shared colour-atlas texture (piece.tex 1..n) → one lifted Lambert material per atlas
//   • embedded — the piece keeps the material(s) baked into its own glb (packs whose models use several materials: walls, planks, wood…)
//                (manifest.embedded: true) — every material is lifted by the land's brightness and drawn as a Lambert material.
// A land's builder shape is shared with the placeholder: async build → { root, spawn, walk, tick }.
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { fixGltfScene } from './depthFix.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { GROUND_Y } from './hexGrid.js'
import { createWalker } from './landWalk.js'
import { loadNav } from './capitalNav.js'

function paletteTexture(url, srgb = true) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(url, (t) => {
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
      t.flipY = false                       // glTF UV convention
      t.wrapS = t.wrapT = THREE.RepeatWrapping
      t.magFilter = THREE.NearestFilter     // crisp palette swatches
      t.minFilter = THREE.NearestFilter
      t.generateMipmaps = false
      resolve(t)
    }, undefined, reject)
  })
}

// The manifest for a land, or null if it has none (then the placeholder land stands in). A missing file may come back as the site's
// index.html with a 200, so check that it really is JSON.
export async function fetchLandManifest(id) {
  try {
    const r = await fetch(`/models/${id}/land.json`)
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return null
    return await r.json()
  } catch { return null }
}

// See-through cutaway: the iso camera looks across the land at a slant, so tall ground, trees or roofs between it and the cat would hide
// the cat. Every material of the land fades out (a dithered hole) in a tube around the sight line from the cat to the camera — but only
// for things clearly ABOVE the cat, so the ground it stands on is never cut. The land's tick feeds the uniforms each frame.
const cutU = { hero: { value: new THREE.Vector3(0, -999, 0) }, dir: { value: new THREE.Vector3(1, 1, 1).normalize() }, r: { value: 0.9 } }
function addCutaway(m) {
  const prev = m.onBeforeCompile
  const baseKey = prev ? m.customProgramCacheKey() : ''
  m.customProgramCacheKey = () => baseKey + '|land-cutaway'
  m.onBeforeCompile = (sh, renderer) => {
    prev?.(sh, renderer)
    sh.uniforms.uCutHero = cutU.hero; sh.uniforms.uCutDir = cutU.dir; sh.uniforms.uCutR = cutU.r
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCutP;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 cp4 = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          cp4 = instanceMatrix * cp4;
        #endif
        vCutP = (modelMatrix * cp4).xyz;`)
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCutP; uniform vec3 uCutHero; uniform vec3 uCutDir; uniform float uCutR;')
      .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>
        {
          vec3 cv = vCutP - uCutHero;
          float ct = dot(cv, uCutDir);
          if (ct > 0.9 && cv.y > 0.5) {
            float cl = length(cv - ct * uCutDir);
            float cf = smoothstep(uCutR, uCutR * 1.45, cl);
            float cn = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
            if (cn > cf) discard;
          }
        }`)
  }
  return m
}

// Cliffs, mountains and floating islands carry a single flat colour in their UVs (the pack paints them with a runtime shader), so they get
// the same idea here: grass on the faces that look up, the rock texture (sampled from three directions) on the faces that look sideways.
function terrainShader(m, tileWorld, opts = {}, tinted = false) {
  const grassA = new THREE.Color(opts.grassA ?? '#6c9a3a'), grassB = new THREE.Color(opts.grassB ?? '#9ab955')
  const rockTint = new THREE.Color(...(opts.rockTint ?? [1, 1, 1])) // (a land can warm or cool its rock: red sandstone, grey granite)
  // (three.js shares one compiled program between materials whose custom code has the same source text — so say which variant this is)
  m.customProgramCacheKey = () => (tinted ? 'land-terrain-tinted' : 'land-terrain-triplanar')
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uTile = { value: tileWorld }
    sh.uniforms.uGrassA = { value: grassA }
    sh.uniforms.uGrassB = { value: grassB }
    sh.uniforms.uRockTint = { value: rockTint }
    sh.uniforms.uGrassTex = { value: opts.grassTex ?? null }
    sh.uniforms.uGrassTile = { value: opts.grassTile ?? 1 }
    sh.uniforms.uHasGrass = { value: opts.grassTex ? 1 : 0 }
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nvarying vec3 vWN;' + (tinted ? '\nattribute vec3 aTint;\nvarying vec3 vTint;' : ''))
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        vec3 wn3 = objectNormal;
        #ifdef USE_INSTANCING
          wn3 = mat3(instanceMatrix) * wn3;
        #endif
        vWN = normalize(mat3(modelMatrix) * wn3);`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vec4 wp4 = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          wp4 = instanceMatrix * wp4;
        #endif
        vWPos = (modelMatrix * wp4).xyz;` + (tinted ? '\n vTint = aTint;' : ''))
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec3 vWPos; varying vec3 vWN; uniform float uTile; uniform vec3 uGrassA; uniform vec3 uGrassB; uniform vec3 uRockTint; uniform sampler2D uGrassTex; uniform float uGrassTile; uniform float uHasGrass;${tinted ? ' varying vec3 vTint;' : ''}
        float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y); }`)
      .replace('#include <map_fragment>', `
        vec3 tn = normalize(vWN);
        float tTop = smoothstep(0.52, 0.74, tn.y);
        vec3 tq = vWPos / uTile;
        vec3 tw = pow(abs(tn), vec3(4.0)); tw /= (tw.x + tw.y + tw.z);
        vec3 tRock = texture2D(map, tq.zy).rgb * tw.x + texture2D(map, tq.xz).rgb * tw.y + texture2D(map, tq.xy).rgb * tw.z;
        vec3 tGrass = ${tinted ? 'vTint * mix(0.9, 1.1, vn(vWPos.xz * 2.3))' : 'mix(uGrassA, uGrassB, vn(vWPos.xz * 1.7))'};
        if (uHasGrass > 0.5) tGrass = texture2D(uGrassTex, vWPos.xz / uGrassTile).rgb * mix(0.9, 1.1, vn(vWPos.xz * 0.9));
        diffuseColor.rgb *= mix(tRock * uRockTint, tGrass, tTop);`)
  }
}

// Night glows: warm additive halos around lanterns, braziers and fire pits (placed by the land builder, layout.glows), which fade in with the
// night and flicker where there is a flame. One Points object; sized in screen pixels from the camera's zoom so they scale with the view.
function buildGlows(list, S) {
  const n = list.length
  const pos = new Float32Array(n * 3), size = new Float32Array(n), col = new Float32Array(n * 3), phase = new Float32Array(n)
  const c = new THREE.Color()
  list.forEach(([x, y, z, r, color, flick], i) => {
    pos.set([x, y, z], i * 3); size[i] = r * 2 * S
    c.set(color); col.set([c.r, c.g, c.b], i * 3); phase[i] = flick ? 0.2 + ((i * 0.6180339) % 1) * 0.8 : -1
  })
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('aSize', new THREE.BufferAttribute(size, 1))
  g.setAttribute('aColor', new THREE.BufferAttribute(col, 3)); g.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1))
  const u = { uPx: { value: 100 }, uNight: { value: 0 }, uTime: { value: 0 } }
  const m = new THREE.ShaderMaterial({
    uniforms: u, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: `attribute float aSize; attribute vec3 aColor; attribute float aPhase; uniform float uPx; uniform float uNight; uniform float uTime; varying vec3 vCol; varying float vA;
      void main() {
        float f = aPhase < 0.0 ? 1.0 : 0.88 + 0.12 * sin(uTime * (6.0 + aPhase * 5.0) + aPhase * 40.0) + 0.05 * sin(uTime * 17.0 + aPhase * 91.0);
        vCol = aColor; vA = uNight * f;
        gl_PointSize = aSize * uPx * f;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `varying vec3 vCol; varying float vA;
      void main() {
        float r = length(gl_PointCoord - 0.5) * 2.0;
        if (r > 1.0) discard;
        float a = pow(1.0 - r, 2.2);
        gl_FragColor = vec4(vCol * (0.6 + 0.8 * a), a * vA * 0.85);
      }`,
  })
  const pts = new THREE.Points(g, m)
  pts.frustumCulled = false; pts.renderOrder = 5; pts.name = 'glows'
  return { pts, u }
}

// Ambient life that follows the cat: petals or leaves drifting down by day, fireflies wandering at night, windblown dust in dry country.
// manifest.ambient = [{ kind: 'petals'|'leaves'|'dust'|'fireflies', colors: [...], count, size (m), fall (m/s), area (m), height (m) }]. One Points object each,
// animated entirely in the vertex shader (positions wrap inside a box that moves with the cat, so nothing pops in or out).
function buildAmbient(cfg, S) {
  const n = cfg.count ?? 260, A = cfg.area ?? 22, Hh = cfg.height ?? 12
  const rnd = new Float32Array(n * 4), col = new Float32Array(n * 3), c = new THREE.Color()
  const cols = (cfg.colors ?? ['#ffffff']).map((h) => new THREE.Color(h))
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < 4; k++) rnd[i * 4 + k] = Math.random()
    c.copy(cols[i % cols.length]); col.set([c.r, c.g, c.b], i * 3)
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3))
  g.setAttribute('aRand', new THREE.BufferAttribute(rnd, 4)); g.setAttribute('aColor', new THREE.BufferAttribute(col, 3))
  const firefly = cfg.kind === 'fireflies'
  const u = { uCenter: { value: new THREE.Vector3() }, uTime: { value: 0 }, uNight: { value: 0 }, uPx: { value: 100 },
    uA: { value: A * S }, uH: { value: Hh * S }, uSize: { value: (cfg.size ?? 0.25) * S }, uFall: { value: (cfg.fall ?? 0.9) * S }, uSway: { value: (cfg.sway ?? 1.6) * S }, uFire: { value: firefly ? 1 : 0 } }
  const m = new THREE.ShaderMaterial({
    uniforms: u, transparent: true, depthWrite: false, blending: firefly ? THREE.AdditiveBlending : THREE.NormalBlending,
    vertexShader: `attribute vec4 aRand; attribute vec3 aColor; uniform vec3 uCenter; uniform float uTime, uNight, uPx, uA, uH, uSize, uFall, uSway, uFire; varying vec3 vCol; varying float vA; varying float vRot;
      void main() {
        float t = uTime;
        vec3 p;
        float wrapX = mod(aRand.x * 2.0 * uA + t * (0.35 * uSway) + sin(t * 0.6 + aRand.w * 20.0) * uSway - uCenter.x + uA, 2.0 * uA) - uA;
        float wrapZ = mod(aRand.z * 2.0 * uA + t * (0.18 * uSway) + cos(t * 0.5 + aRand.y * 20.0) * uSway - uCenter.z + uA, 2.0 * uA) - uA;
        float fade = 1.0;
        if (uFire > 0.5) {  // fireflies hover and meander low over the ground
          p = vec3(uCenter.x + wrapX, uCenter.y + (0.4 + aRand.y * 3.0) * (uH / 12.0) + sin(t * 0.9 + aRand.w * 30.0) * 0.18 * uSway, uCenter.z + wrapZ);
          fade = pow(max(0.0, sin(t * (0.6 + aRand.w) + aRand.x * 50.0)), 2.0) * uNight;
        } else {
          float y = mod(aRand.y * uH - t * uFall * (0.6 + aRand.w * 0.8), uH);
          p = vec3(uCenter.x + wrapX, uCenter.y + y, uCenter.z + wrapZ);
          fade = smoothstep(0.0, 0.12 * uH, y) * smoothstep(uH, 0.88 * uH, y) * (1.0 - 0.55 * uNight);
        }
        float edge = 1.0 - smoothstep(0.7 * uA, uA, length(vec2(wrapX, wrapZ)));
        vCol = aColor; vA = fade * edge; vRot = aRand.w * 6.28 + t * (0.8 + aRand.x);
        gl_PointSize = uSize * uPx * (0.7 + aRand.x * 0.6);
        gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
      }`,
    fragmentShader: `varying vec3 vCol; varying float vA; varying float vRot; uniform float uFire;
      void main() {
        vec2 d = gl_PointCoord - 0.5;
        if (uFire > 0.5) { float r = length(d) * 2.0; if (r > 1.0) discard; gl_FragColor = vec4(vCol, pow(1.0 - r, 1.6) * vA); return; }
        float cs = cos(vRot), sn = sin(vRot); d = vec2(cs * d.x - sn * d.y, sn * d.x + cs * d.y);
        float a = 1.0 - smoothstep(0.18, 0.5, length(d * vec2(1.0, 2.1)));   // a small petal / leaf / mote
        if (a < 0.02) discard;
        gl_FragColor = vec4(vCol, a * vA);
      }`,
  })
  const pts = new THREE.Points(g, m)
  pts.frustumCulled = false; pts.renderOrder = 6; pts.name = 'ambient-' + (cfg.kind ?? 'petals')
  return { pts, u }
}

// Water: a see-through sheet with a deep-to-shallow gradient, a foam line at the shore, slow ripples and sun glints; it dims at night.
function waterMaterial(color, alpha, ellipse) {
  const u = THREE.UniformsUtils.merge([THREE.UniformsLib.fog, { uCol: { value: new THREE.Color(color) }, uAlpha: { value: alpha }, uTime: { value: 0 }, uNight: { value: 0 }, uShape: { value: ellipse === 2 ? 2 : ellipse ? 1 : 0 } }])
  return new THREE.ShaderMaterial({
    uniforms: u, transparent: true, depthWrite: false, fog: true,
    vertexShader: `uniform float uShape; varying vec2 vUv; varying vec3 vW; varying float vEdge;
      #include <fog_pars_vertex>
      void main() {
        vUv = uv; vec4 wp = modelMatrix * vec4(position, 1.0); vW = wp.xyz;
        vEdge = uShape > 1.5 ? 0.5 : uShape > 0.5 ? length(uv - 0.5) * 2.0 : max(abs(uv.x - 0.5), abs(uv.y - 0.5)) * 2.0;
        vec4 mvPosition = viewMatrix * wp; gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: `uniform vec3 uCol; uniform float uAlpha, uTime, uNight; varying vec2 vUv; varying vec3 vW; varying float vEdge;
      #include <fog_pars_fragment>
      float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f); return mix(mix(h21(i), h21(i + vec2(1, 0)), f.x), mix(h21(i + vec2(0, 1)), h21(i + vec2(1, 1)), f.x), f.y); }
      void main() {
        vec2 q = vW.xz * 3.2;
        float rip = vn(q + vec2(uTime * 0.35, uTime * 0.22)) * 0.6 + vn(q * 2.1 - vec2(uTime * 0.3, uTime * 0.41)) * 0.4;
        vec3 deep = uCol * 0.62, shallow = mix(uCol, vec3(0.78, 0.95, 0.92), 0.38);
        vec3 c = mix(deep, shallow, smoothstep(0.35, 1.0, vEdge));
        c += (rip - 0.5) * 0.16;
        float glint = smoothstep(0.86, 0.98, vn(q * 3.4 + uTime * 0.9)) * (1.0 - uNight * 0.7);
        c += glint * 0.55;
        float foam = smoothstep(0.9, 0.985, vEdge) * (0.55 + 0.45 * vn(q * 2.0 + uTime * 0.5));
        c = mix(c, vec3(0.95, 0.98, 0.95), foam * 0.8);
        c *= mix(1.0, 0.42, uNight);
        float a = mix(uAlpha, 0.96, smoothstep(0.82, 1.0, vEdge));
        gl_FragColor = vec4(c, a);
        #include <fog_fragment>
      }`,
  })
}

// A Unity-style painted terrain: three RGBA "splat" maps say how much of each of up to ten ground textures (grass, dirt, rock, cobblestone…)
// covers every spot; each texture tiles every `tile` metres. Drawn with a Lambert material whose colour is swapped for that blend.
function splatMaterial(splats, layerTex, tiles, tsize, rockLayer = 4) {
  const m = new THREE.MeshLambertMaterial({ color: 0xffffff })
  m.customProgramCacheKey = () => 'land-splat'
  m.onBeforeCompile = (sh) => {
    splats.forEach((t, i) => { sh.uniforms['uSplat' + i] = { value: t } })
    layerTex.forEach((t, i) => { sh.uniforms['uL' + i] = { value: t } })
    sh.uniforms.uTile = { value: tiles }
    sh.uniforms.uTSize = { value: tsize } // x0, z0, width, depth of the terrain in metres
    const n = layerTex.length
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vTP; varying vec3 vTPos; varying vec3 vTN;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n vTP = position.xz; vTPos = position; vTN = normal;')
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
        varying vec2 vTP; varying vec3 vTPos; varying vec3 vTN; uniform vec4 uTSize; uniform float uTile[${n}];
        uniform sampler2D uSplat0, uSplat1, uSplat2;
        ${layerTex.map((_, i) => `uniform sampler2D uL${i};`).join(' ')}`)
      .replace('#include <map_fragment>', `
        vec2 suv = (vTP - uTSize.xy) / uTSize.zw;
        vec4 s0 = texture2D(uSplat0, suv), s1 = texture2D(uSplat1, suv), s2 = texture2D(uSplat2, suv);
        float w[12]; w[0]=s0.r; w[1]=s0.g; w[2]=s0.b; w[3]=s0.a; w[4]=s1.r; w[5]=s1.g; w[6]=s1.b; w[7]=s1.a; w[8]=s2.r; w[9]=s2.g; w[10]=s2.b; w[11]=s2.a;
        float tot = 0.0; for (int k = 0; k < ${n}; k++) tot += w[k];
        vec3 tc = vec3(0.0);
        ${layerTex.map((_, i) => `if (w[${i}] > 0.003) tc += texture2D(uL${i}, vTP / uTile[${i}]).rgb * w[${i}];`).join('\n        ')}
        vec3 painted = tc / max(tot, 0.0001);
        // steep faces (the terrain's own cliffs) are bare rock, whatever the paint says: the rock layer (index ${rockLayer}), sampled from three sides
        vec3 tn = normalize(vTN);
        float steep = smoothstep(0.82, 0.56, tn.y);
        vec3 tw = pow(abs(tn), vec3(4.0)); tw /= (tw.x + tw.y + tw.z);
        vec3 rk = texture2D(uL${rockLayer}, vTPos.zy / (uTile[${rockLayer}] * 1.6)).rgb * tw.x + texture2D(uL${rockLayer}, vTPos.xz / (uTile[${rockLayer}] * 1.6)).rgb * tw.y + texture2D(uL${rockLayer}, vTPos.xy / (uTile[${rockLayer}] * 1.6)).rgb * tw.z;
        diffuseColor.rgb *= mix(painted, rk * 0.92, steep);`)
  }
  return m
}

async function loadTexture(url, { srgb = true, repeat = false, mip = true } = {}) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(url, (t) => {
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
      t.flipY = false
      t.wrapS = t.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping
      t.generateMipmaps = mip; t.minFilter = mip ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter; t.magFilter = THREE.LinearFilter
      t.anisotropy = 4
      resolve(t)
    }, undefined, reject)
  })
}

// The terrain as square chunks (so off-screen ground is culled): indexed grids that split every cell exactly the way terrainMath.heightAt does.
function terrainChunks(T, material, chunkCells = 48) {
  const group = new THREE.Group()
  const { x0, z0, cell, nx, nz } = T
  const norm = (i, j, out) => { // smooth normal by central differences (so chunk seams match)
    const hl = T.H(i - 1, j), hr = T.H(i + 1, j), hd = T.H(i, j - 1), hu = T.H(i, j + 1)
    out.set(hl - hr, 2 * cell, hd - hu).normalize()
  }
  const v = new THREE.Vector3()
  for (let cj = 0; cj < nz - 1; cj += chunkCells) for (let ci = 0; ci < nx - 1; ci += chunkCells) {
    const w = Math.min(chunkCells, nx - 1 - ci), h = Math.min(chunkCells, nz - 1 - cj)
    const pos = new Float32Array((w + 1) * (h + 1) * 3), nrm = new Float32Array((w + 1) * (h + 1) * 3), idx = new Uint32Array(w * h * 6)
    let o = 0
    for (let j = 0; j <= h; j++) for (let i = 0; i <= w; i++) {
      const gi = ci + i, gj = cj + j
      pos[o] = x0 + gi * cell; pos[o + 1] = T.H(gi, gj); pos[o + 2] = z0 + gj * cell
      norm(gi, gj, v); nrm[o] = v.x; nrm[o + 1] = v.y; nrm[o + 2] = v.z; o += 3
    }
    let k = 0
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
      const a = j * (w + 1) + i, b = a + 1, c = a + (w + 1), d = c + 1
      idx[k++] = a; idx[k++] = c; idx[k++] = b   // a c b
      idx[k++] = b; idx[k++] = c; idx[k++] = d   // b c d
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('normal', new THREE.BufferAttribute(nrm, 3)); g.setIndex(new THREE.BufferAttribute(idx, 1))
    g.computeBoundingSphere()
    const mesh = new THREE.Mesh(g, material); mesh.name = 'ground'
    group.add(mesh)
  }
  return group
}

export async function buildModelLand(def, manifest, progress = () => {}) {
  let pGlb = 0, pLay = 0, pTex = 0
  const report = () => progress(0.62 * pGlb + 0.1 * pLay + 0.18 * pTex)

  const loader = new GLTFLoader()
  loader.setMeshoptDecoder(MeshoptDecoder)
  const glbP = loader.loadAsync(manifest.piecesUrl, (e) => { if (e.lengthComputable) { pGlb = e.loaded / e.total; report() } }).then((g) => { fixGltfScene(g.scene); pGlb = 1; report(); return g })
  const layP = fetch(manifest.layoutUrl).then((r) => { if (!r.ok) throw new Error(`layout ${r.status}`); return r.json() }).then((j) => { pLay = 1; report(); return j })
  const palUrls = manifest.embedded ? [] : manifest.palettes ?? []
  let texDone = 0
  const texTick = (t) => { texDone += 1; pTex = texDone / (palUrls.length + 1); report(); return t }
  const palP = Promise.all(palUrls.map((u) => paletteTexture(u).then(texTick)))
  const emiP = manifest.emissionUrl ? paletteTexture(manifest.emissionUrl).then(texTick).catch(() => null) : Promise.resolve(null)
  const navP = manifest.navUrl ? loadNav(manifest.navUrl).catch((err) => { console.warn('[land] no floor map, walking by footprints:', err.message); return null }) : Promise.resolve(null)
  const [gltf, layout, palettes, emission, nav] = await Promise.all([glbP, layP, palP, emiP, navP])
  // A Unity-style painted terrain (heights + ground-paint maps) comes as separate files next to the layout.
  let splat = null
  const tcfg = manifest.terrain
  if (tcfg?.kind === 'splat') {
    const [hbuf, sp, lt] = await Promise.all([
      fetch(`${tcfg.url}/heights.bin`).then((r) => { if (!r.ok) throw new Error(`heights ${r.status}`); return r.arrayBuffer() }),
      Promise.all([0, 1, 2].map((i) => loadTexture(`${tcfg.url}/splat${i}.png`, { srgb: false }))),
      Promise.all(tcfg.layers.map((l) => loadTexture(`${tcfg.url}/${l.file}`, { repeat: true }))),
    ])
    layout.terrain = { x0: tcfg.x0, z0: tcfg.z0, cell: tcfg.cell, nx: tcfg.n, nz: tcfg.n, h: new Int16Array(hbuf), hs: tcfg.yScale, hb: tcfg.yBase }
    splat = { splats: sp, layers: lt, tiles: tcfg.layers.map((l) => l.tile) }
  }

  const brightness = manifest.brightness ?? 1.95
  const S = manifest.scale ?? 0.24
  const groundLevelM = manifest.groundLevelM ?? 0
  const materials = [] // every material we made, so the night glow can reach it
  const lift = (m) => { m.color?.multiplyScalar?.(brightness); return m }

  // palette mode: one lifted Lambert material per atlas (all sharing the night-glow map: off by day, on at night)
  const paletteMats = palettes.map((tex) => {
    const m = new THREE.MeshLambertMaterial({ map: tex, emissive: emission ? 0xffffff : 0x000000, emissiveMap: emission, emissiveIntensity: 0, side: THREE.DoubleSide })
    m.color.setScalar(brightness)
    addCutaway(m)
    materials.push(m)
    return m
  })
  // embedded mode: turn each baked material into a lifted Lambert one (once per source material). The exporter tags every material with a
  // "mode" (scripts/adventure/export_empire.py): opaque · mask (leaf / plant cut-outs) · blend (water, mist) · triplanar (cliffs and mountains:
  // grass on the tops, rock on the sides). Materials with a glow map (lanterns, windows) light up at night like the Capital's.
  const embedded = new Map()
  const embeddedMat = (src) => {
    let m = embedded.get(src)
    if (m) return m
    const mode = src.userData?.mode ?? 'opaque'
    const glow = !!src.emissiveMap
    m = lift(new THREE.MeshLambertMaterial({
      map: src.map ?? null, color: src.color ?? 0xffffff, side: THREE.DoubleSide, vertexColors: !!src.vertexColors, // (a land arranged in Blender may paint its ground per vertex)
      alphaTest: mode === 'mask' ? 0.45 : 0, transparent: mode === 'blend', opacity: mode === 'blend' ? (src.opacity ?? 1) : 1, depthWrite: mode !== 'blend',
      emissive: glow ? 0xffffff : 0x000000, emissiveMap: src.emissiveMap ?? null, emissiveIntensity: 0,
    }))
    const tint = src.userData?.tint
    if (Array.isArray(tint)) m.color.multiply(new THREE.Color(tint[0], tint[1], tint[2])) // (per-material colour nudge from slot_map.json)
    if (mode === 'triplanar') terrainShader(m, 8 * manifest.scale, splat ? { ...manifest.terrain, grassTex: splat.layers[0], grassTile: (splat.tiles[0] ?? 5) * 1.4 * manifest.scale } : manifest.terrain)
    if (mode !== 'blend') addCutaway(m)
    embedded.set(src, m); materials.push(m)
    return m
  }

  const root = new THREE.Group()
  root.userData.isBiome = true // keeps the home-hiding code (and the old adventure scene) from touching it
  root.userData.landId = def.id
  root.visible = false
  root.scale.setScalar(S)
  const m0 = layout.meta
  root.position.set(-m0.spawn.x * S, GROUND_Y - groundLevelM * S, -m0.spawn.z * S)

  const R = manifest.radiusM ?? 60
  const walker = createWalker(layout, manifest, nav) // (the walking rules)
  const T = walker.T
  // a wide flat disc far underneath: it is the horizon beyond the ground grid
  const ground = new THREE.Mesh(new THREE.CircleGeometry(R + 400, 48).rotateX(-Math.PI / 2), new THREE.MeshLambertMaterial({ color: manifest.groundColor ?? '#78904a' }))
  ground.position.set(0, manifest.groundDiscY ?? (T ? T.hMin - 0.6 : -0.08), 0) // under the pieces' bases (they rest on y = 0); a land with floating islands sets it far below
  root.add(ground)

  if (T && splat) {
    const sm = lift(splatMaterial(splat.splats, splat.layers, splat.tiles, new THREE.Vector4(T.x0, T.z0, (T.nx - 1) * T.cell, (T.nz - 1) * T.cell)))
    addCutaway(sm)
    root.add(terrainChunks(T, sm))
    materials.push(sm)
  } else if (T) { // the ground: a faceted grid mesh, grass (painted per vertex) on the tops, rock on the steep faces
    const { x0, z0, cell, nx, nz } = T
    const pos = new Float32Array((nx - 1) * (nz - 1) * 18), tint = new Float32Array((nx - 1) * (nz - 1) * 18)
    const col = new THREE.Color(), tc = (i, j) => { const v = layout.terrain.c?.[j * nx + i] ?? 0x6c9a3a; return col.setRGB(((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255, THREE.SRGBColorSpace) }
    let o = 0
    const vert = (i, j) => { pos[o] = x0 + i * cell; pos[o + 1] = T.H(i, j); pos[o + 2] = z0 + j * cell; const c = tc(i, j); tint[o] = c.r; tint[o + 1] = c.g; tint[o + 2] = c.b; o += 3 }
    for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
      vert(i, j); vert(i, j + 1); vert(i + 1, j)         // a c b
      vert(i + 1, j); vert(i, j + 1); vert(i + 1, j + 1) // b c d
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    g.setAttribute('aTint', new THREE.BufferAttribute(tint, 3))
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2))
    g.computeVertexNormals()
    g.computeBoundingSphere()
    const rock = manifest.terrain?.rockUrl ? await paletteTexture(manifest.terrain.rockUrl) : null
    if (rock) { rock.magFilter = THREE.LinearFilter; rock.minFilter = THREE.LinearMipmapLinearFilter; rock.generateMipmaps = true }
    const tm = lift(new THREE.MeshLambertMaterial({ map: rock, color: 0xffffff }))
    terrainShader(tm, 8 * S, manifest.terrain, true)
    addCutaway(tm)
    const mesh = new THREE.Mesh(g, tm)
    mesh.name = 'ground'
    root.add(mesh)
    materials.push(tm)
  }
  const ambients = (manifest.ambient ?? []).map((cfg) => buildAmbient(cfg, S))
  for (const a of ambients) root.add(a.pts)
  const glows = layout.glows?.length ? buildGlows(layout.glows, S) : null
  if (glows) root.add(glows.pts)
  const waterList = layout.water ?? []
  const waterMats = []
  for (const w of waterList) { // ponds: a flat see-through sheet
    const geo = w.r ? new THREE.CircleGeometry(w.r, 48) : w.rx ? new THREE.CircleGeometry(1, 56).scale(w.rx, w.rz, 1) : new THREE.PlaneGeometry(w.hx * 2, w.hz * 2)
    geo.rotateX(-Math.PI / 2)
    const wmat = waterMaterial(w.color ?? '#2f8a93', w.alpha ?? 0.8, w.calm ? 2 : !!(w.r || w.rx)); waterMats.push(wmat)
    const wm = new THREE.Mesh(geo, wmat)
    wm.position.set(w.x, w.y, w.z); wm.rotation.y = (w.yawDeg ?? 0) * Math.PI / 180
    wm.renderOrder = 2; wm.name = 'water'
    root.add(wm)
  }

  const detailMeshes = [] // chunks of small pieces: hidden when the cat is far from them (see tick)
  // Instanced pieces, bucketed into spatial chunks for frustum culling.
  const chunkM = manifest.chunkM ?? 60
  const nodeM = new THREE.Matrix4(), instM = new THREE.Matrix4(), jit = new THREE.Color()
  for (const piece of layout.pieces) {
    const node = gltf.scene.getObjectByName(piece.id)
    if (!node) continue
    const meshes = []
    node.traverse((o) => { if (o.isMesh) meshes.push(o) }) // (a multi-material model arrives as several meshes)
    for (const mesh of meshes) {
      mesh.updateWorldMatrix(true, false)
      nodeM.copy(mesh.matrixWorld) // includes any quantisation scale baked in by the optimiser
      const mat = manifest.embedded ? embeddedMat(mesh.material) : (paletteMats[(piece.tex ?? 1) - 1] ?? paletteMats[0])
      const buckets = new Map()
      for (let i = 0; i < piece.count; i++) {
        instM.fromArray(piece.m, i * 16)
        const key = `${Math.floor(piece.m[i * 16 + 12] / chunkM)},${Math.floor(piece.m[i * 16 + 14] / chunkM)}`
        let list = buckets.get(key)
        if (!list) buckets.set(key, (list = []))
        list.push(new THREE.Matrix4().multiplyMatrices(instM, nodeM))
      }
      const vary = /Tree_|Bush|Bamboo|Fern|Grass|Flower|Moss|Pile|Lilli/.test(piece.name) // no two trees quite alike
      mesh.geometry.computeBoundingBox()
      const bb = mesh.geometry.boundingBox, dim = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z) * Math.max(Math.abs(nodeM.elements[0]), Math.abs(nodeM.elements[5]), 1e-6)
      const isDetail = dim < (manifest.detailSizeM ?? 2.6) // small things (furniture, lanterns, gravel, flowers…) are only drawn near the cat
      for (const list of buckets.values()) {
        const im = new THREE.InstancedMesh(mesh.geometry, mat, list.length)
        list.forEach((mm, i) => {
          im.setMatrixAt(i, mm)
          if (vary) { const k = mm.elements[12] * 12.9898 + mm.elements[14] * 78.233, f = (a) => { const v = Math.sin(k * a) * 43758.5453; return v - Math.floor(v) }; im.setColorAt(i, jit.setRGB(1 + (f(1) - 0.5) * 0.34, 1 + (f(2.7) - 0.5) * 0.24, 1 + (f(5.1) - 0.5) * 0.34)) }
        })
        im.instanceMatrix.needsUpdate = true
        im.computeBoundingSphere()
        im.name = piece.name
        if (isDetail) { im.userData.detail = true; detailMeshes.push(im) }
        root.add(im)
      }
    }
  }

  const cols = layout.colliders ?? []
  const { floorM } = walker
  const walk = (wx, wz, wy = null) => {
    const y = floorM((wx - root.position.x) / S, (wz - root.position.z) / S, wy === null ? null : (wy - root.position.y) / S)
    return y === null ? null : root.position.y + y * S
  }

  // Spawn: the layout's spawn point (the world origin), nudged to the nearest spot the hero can stand.
  let spawn = { x: 0, y: GROUND_Y, z: 0 }
  if (walk(0, 0) === null) {
    search: for (let r = 0.1; r < 4; r += 0.1) for (let a = 0; a < Math.PI * 2; a += Math.PI / 8) {
      const x = Math.cos(a) * r, z = Math.sin(a) * r
      if (walk(x, z) !== null) { spawn = { x, y: walk(x, z), z }; break search }
    }
  } else spawn.y = walk(0, 0)

  const tick = (_dt, hx, hz, night = 0, view = null) => { // lanterns / windows glow as night falls; the cutaway follows the cat
    if (glows) { glows.u.uNight.value = Math.max(0, Math.min(1, night)); glows.u.uTime.value = performance.now() / 1000; if (view?.px) glows.u.uPx.value = view.px }
    if (detailMeshes.length) { // distance cull of the small stuff: a chunk is drawn only if the cat is within reach of it
      const reach = manifest.detailReachM ?? 48 // metres
      for (const im of detailMeshes) {
        const b = im.boundingSphere
        im.visible = Math.hypot(b.center.x * S + root.position.x - hx, b.center.z * S + root.position.z - hz) < (reach + b.radius) * S
      }
    }
    for (const wm of waterMats) { wm.uniforms.uTime.value = performance.now() / 1000; wm.uniforms.uNight.value = Math.max(0, Math.min(1, night)) }
    for (const a of ambients) { a.u.uTime.value = performance.now() / 1000; a.u.uNight.value = Math.max(0, Math.min(1, night)); if (view) { a.u.uCenter.value.set(hx, view.y, hz); if (view.px) a.u.uPx.value = view.px } }
    if (view?.cam) { cutU.hero.value.set(hx, view.y, hz); cutU.dir.value.copy(view.cam).sub(cutU.hero.value).normalize() }
    if (!emission && !materials.some((m) => m.emissiveMap)) return
    const glow = Math.max(0, Math.min(1, night)) * 1.6
    for (const m of materials) m.emissiveIntensity = glow
  }
  progress(1)
  const heightAt = walker.heightAt // (metres, in the land's own frame)
  return { root, spawn, walk, tick, heightAt, nav, floorM: walker.floorM, mood: manifest.mood ?? null, kind: 'model', instanced: root.children.filter((c) => c.isInstancedMesh).length, colliders: cols.length }
}
